import React, { useState } from 'react';
import { useData } from '../../context/DataContext';
import { exportStudentsToCSV } from '../../utils/exportUtils';

export const StudentDirectoryTable = ({ onStudentClick }) => {
  const { getScopedStudents } = useData();
  const [searchQuery, setSearchQuery] = useState('');
  const [gradeFilter, setGradeFilter] = useState('all');
  const [sectionFilter, setSectionFilter] = useState('all');
  const [schoolFilter, setSchoolFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all'); // 'all' | 'participated' | 'absent'

  const scoped = getScopedStudents();

  const grades = [...new Set(scoped.map(s => s.registeredGrade))].sort((a, b) => a - b);
  const sections = [...new Set(scoped.map(s => s.section).filter(Boolean))].sort();
  const schools = [...new Set(scoped.map(s => s.school).filter(Boolean))].sort();

  const getFilteredStudents = () => {
    let list = [...scoped];

    if (gradeFilter !== 'all') {
      list = list.filter(s => s.registeredGrade === parseInt(gradeFilter));
    }
    if (sectionFilter !== 'all') {
      list = list.filter(s => s.section === sectionFilter);
    }
    if (schoolFilter !== 'all') {
      list = list.filter(s => s.school === schoolFilter);
    }
    if (statusFilter === 'participated') {
      list = list.filter(s => s.participated);
    } else if (statusFilter === 'absent') {
      list = list.filter(s => !s.participated);
    }

    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      list = list.filter(
        s => s.name.toLowerCase().includes(q) || s.id.toLowerCase().includes(q)
      );
    }

    return list.sort((a, b) => a.name.localeCompare(b.name));
  };

  const students = getFilteredStudents();

  const handleExport = () => {
    exportStudentsToCSV(students, 'Student_Directory');
  };

  const handleClearFilters = () => {
    setSearchQuery('');
    setGradeFilter('all');
    setSectionFilter('all');
    setSchoolFilter('all');
    setStatusFilter('all');
  };

  const getMasteryColor = (level, grade) => {
    const target = Math.min(7, grade);
    const gap = target - level;
    if (gap <= 0) return '#166534'; // Green
    if (gap <= 2) return '#b45309'; // Amber
    return '#b91c1c'; // Red
  };

  return (
    <div className="bg-white p-6 rounded-2xl shadow-lg border-2 border-gray-100 mb-8 select-none">
      <div className="flex justify-between items-center mb-6 flex-wrap gap-4">
        <h3 className="text-xl font-bold text-gray-800">Student Directory</h3>
        <div className="flex gap-2 flex-wrap items-center">
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search student name or ID..."
            className="px-4 py-2 border-2 border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none w-64"
          />
          <select
            value={gradeFilter}
            onChange={(e) => setGradeFilter(e.target.value)}
            className="px-3 py-2 border-2 border-gray-300 rounded-lg text-sm bg-white focus:ring-2 focus:ring-blue-500 outline-none cursor-pointer"
          >
            <option value="all">All Grades</option>
            {grades.map(g => (
              <option key={g} value={g}>
                Grade {g}
              </option>
            ))}
          </select>
          <select
            value={sectionFilter}
            onChange={(e) => setSectionFilter(e.target.value)}
            className="px-3 py-2 border-2 border-gray-300 rounded-lg text-sm bg-white focus:ring-2 focus:ring-blue-500 outline-none cursor-pointer"
          >
            <option value="all">All Sections</option>
            {sections.map(s => (
              <option key={s} value={s}>
                Section {s}
              </option>
            ))}
          </select>
          <select
            value={schoolFilter}
            onChange={(e) => setSchoolFilter(e.target.value)}
            disabled={schools.length <= 1}
            className="px-3 py-2 border-2 border-gray-300 rounded-lg text-sm bg-white focus:ring-2 focus:ring-blue-500 outline-none cursor-pointer disabled:opacity-50"
          >
            <option value="all">All Schools</option>
            {schools.map(s => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
          <button
            onClick={handleExport}
            disabled={students.length === 0}
            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 font-bold transition-colors cursor-pointer disabled:opacity-50"
          >
            📥 Export Excel
          </button>
          <button
            onClick={() => setStatusFilter('participated')}
            className={`px-3 py-2 rounded-lg font-bold text-sm transition-colors cursor-pointer ${
              statusFilter === 'participated'
                ? 'bg-green-600 text-white'
                : 'bg-green-100 text-green-700 hover:bg-green-200'
            }`}
          >
            Participated
          </button>
          <button
            onClick={() => setStatusFilter('absent')}
            className={`px-3 py-2 rounded-lg font-bold text-sm transition-colors cursor-pointer ${
              statusFilter === 'absent'
                ? 'bg-red-600 text-white'
                : 'bg-red-100 text-red-700 hover:bg-red-200'
            }`}
          >
            Absent
          </button>
          <button
            onClick={handleClearFilters}
            className="px-3 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 font-bold text-sm transition-colors cursor-pointer"
          >
            Clear Filter
          </button>
        </div>
      </div>

      <div className="overflow-x-auto rounded-xl border border-gray-200 max-h-[500px] overflow-y-auto">
        <table className="w-full text-sm border-collapse">
          <thead className="sticky top-0 bg-blue-600 text-white z-10">
            <tr>
              <th className="py-4 px-4 text-left border-b font-bold">Student Name</th>
              <th className="py-4 px-4 text-left border-b font-bold">Grade</th>
              <th className="py-4 px-4 text-left border-b font-bold">Section</th>
              <th className="py-4 px-4 text-left border-b font-bold">School</th>
              <th className="py-4 px-4 text-center border-b font-bold">English Mastery</th>
              <th className="py-4 px-4 text-center border-b font-bold">Math Mastery</th>
              <th className="py-4 px-4 text-center border-b font-bold">Status</th>
            </tr>
          </thead>
          <tbody>
            {students.length === 0 ? (
              <tr>
                <td colSpan="7" className="py-12 text-center text-gray-400 font-bold italic">
                  No students found matching current filters.
                </td>
              </tr>
            ) : (
              students.map((student) => {
                const isParticipated = student.participated;
                const engColor = isParticipated ? getMasteryColor(student.englishMastery, student.registeredGrade) : '#6b7280';
                const mathColor = isParticipated ? getMasteryColor(student.mathMastery, student.registeredGrade) : '#6b7280';

                return (
                  <tr
                    key={student.id}
                    onClick={() => {
                      if (isParticipated) onStudentClick(student);
                    }}
                    className={`border-b border-gray-100 transition-colors ${
                      isParticipated
                        ? 'hover:bg-gray-50 cursor-pointer'
                        : 'cursor-default bg-gray-50/50'
                    }`}
                  >
                    <td className="py-3 px-4 font-bold text-gray-900">{student.name}</td>
                    <td className="py-3 px-4 text-gray-700 font-medium">{student.registeredGrade}</td>
                    <td className="py-3 px-4 text-gray-700 font-medium">{student.section || '—'}</td>
                    <td className="py-3 px-4 text-gray-600 font-medium">{student.school}</td>
                    <td className="py-3 px-4 text-center font-bold" style={{ color: engColor }}>
                      {isParticipated ? student.englishMastery : '—'}
                    </td>
                    <td className="py-3 px-4 text-center font-bold" style={{ color: mathColor }}>
                      {isParticipated ? student.mathMastery : '—'}
                    </td>
                    <td className="py-3 px-4 text-center">
                      <span
                        className="px-3 py-1 rounded-full text-xs font-bold text-white inline-block shadow-xs"
                        style={{ backgroundColor: isParticipated ? '#16a34a' : '#dc2626' }}
                      >
                        {isParticipated ? '✓ Participated' : '✗ Absent'}
                      </span>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};
export default StudentDirectoryTable;
